import { BlockEditProps } from "@wordpress/blocks";
import { MediaPlaceholder, MediaUpload } from "@wordpress/block-editor";
import React from "react";
import { Placeholder } from "@wordpress/components";
import useIsSelected from "../hooks/useIsSelected";

type Prefixed<T extends Record<string, unknown>, TPrefix extends string> = {
  [K in keyof T as K extends string ? `${TPrefix}${K}` : never]: T[K];
};

type ImageProps = {
  Url?: string | undefined;
  Id?: number | undefined;
  Alt?: string | undefined;
};

export type ImageEditorProps<TPrefix extends string = "image"> = Pick<
  BlockEditProps<Prefixed<ImageProps, TPrefix>>,
  "attributes" | "setAttributes"
> & {
  prefix: TPrefix;
  placeholderClassName?: string;
};

export default function ImageEditor<T extends string>(
  props: ImageEditorProps<T>
) {
  const isSelected = useIsSelected();
  const urlKey = `${props.prefix}Url` as const;
  const id = props.attributes[`${props.prefix}Id`] as number | undefined;
  const url = props.attributes[`${props.prefix}Url`] as string | undefined;
  const alt = props.attributes[`${props.prefix}Alt`] as string | undefined;
  const isSvg = url?.endsWith(".svg") ? true : false;
  return (
    <MediaPlaceholder
      onSelect={(el) => {
        props.setAttributes({
          [urlKey]: el.url as string,
          [`${props.prefix}Alt`]: el.alt as string,
        } as Partial<Prefixed<ImageProps, T>>);
      }}
      onHTMLDrop={undefined}
      // @ts-ignore
      placeholder={(content) =>
        url ? (
          <div className="yt-image-editor-placeholder">
            {isSvg ? (
              <div
                className="svg-icon"
                style={{ WebkitMaskImage: `url(${url})` }}
              ></div>
            ) : null}
            <img src={url} alt={alt} />
            <MediaUpload
              onSelect={(el) => {
                props.setAttributes({
                  [urlKey]: el.url as string,
                  [`${props.prefix}Alt`]: el.alt as string,
                } as Partial<Prefixed<ImageProps, T>>);
              }}
              allowedTypes={["image"]}
              value={id}
              render={({ open }) => (
                <div className="yt-image-editor-toolbar">
                  <button
                    className="components-button editor-post-featured-image__action"
                    onClick={open}
                  >
                    Ersetzen
                  </button>
                  <button
                    className="components-button editor-post-featured-image__action"
                    onClick={() =>
                      props.setAttributes({
                        [`${props.prefix}Url`]: undefined,
                        [`${props.prefix}Alt`]: "",
                      } as Partial<Prefixed<ImageProps, T>>)
                    }
                  >
                    Entfernen
                  </button>
                </div>
              )}
            />
          </div>
        ) : (
          <Placeholder
            withIllustration={!isSelected}
            className={props.placeholderClassName}
            label="Bild auswählen"
          >
            {content}
          </Placeholder>
        )
      }
      className={props.placeholderClassName}
      mediaPreview={<img src={url} alt={alt} />}
      accept="image/*"
    />
  );
}
